import { ACTIVE_TENANT_COOKIE, BASE_DOMAIN, OAUTH_STATE_KEY } from "./constants";
import type { OAuthState } from "./types";

// Allows only same-origin relative paths.
// Rejects: external URLs, protocol-relative //, data:, javascript:, or malformed strings.
const SAFE_REDIRECT_RE = /^\/[a-zA-Z0-9/_\-?=&#]*$/;

export function validateRedirect(raw: unknown): string | null {
  if (typeof raw !== "string" || !raw) return null;
  if (raw.startsWith("http") || raw.startsWith("//")) return null;
  if (!SAFE_REDIRECT_RE.test(raw)) return null;
  return raw;
}

export function storeAuthState(state: Pick<OAuthState, "redirect" | "tenant">): void {
  const full: OAuthState = {
    ...state,
    nonce: crypto.randomUUID(),
    v: 1,
  };
  sessionStorage.setItem(OAUTH_STATE_KEY, JSON.stringify(full));
}

// Reads and removes the stored OAuth state. Returns null on parse failure or version mismatch.
export function consumeAuthState(): OAuthState | null {
  const raw = sessionStorage.getItem(OAUTH_STATE_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as OAuthState;
    if (parsed.v !== 1) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function extractTenant(hostname: string, baseDomain: string): string | null {
  if (!hostname.endsWith(baseDomain)) return null;
  const withoutBase = hostname.slice(0, hostname.length - baseDomain.length);
  const sub = withoutBase.replace(/\.$/, "");
  return sub || null;
}

export function getCurrentTenant(): string | null {
  if (typeof window === "undefined") return null;
  return extractTenant(window.location.hostname, BASE_DOMAIN);
}

// Builds an absolute URL back to the tenant subdomain for cross-origin post-auth navigation.
export function buildTenantUrl(tenant: string, path: string): string {
  const { protocol, port } = window.location;
  const portSuffix = port ? `:${port}` : "";
  return `${protocol}//${tenant}.${BASE_DOMAIN}${portSuffix}${path}`;
}

// Maps a raw OAuth/library error string to a normalized message suitable for the UI.
export function normalizeAuthError(raw: unknown): string {
  if (typeof raw !== "string" || !raw) return "An unexpected authentication error occurred.";
  const lower = raw.toLowerCase();
  if (lower.includes("access_denied")) return "Access was denied. Please try again.";
  if (lower.includes("invalid_client")) return "OAuth client configuration error.";
  if (lower.includes("invalid_grant")) return "Authorization code expired or already used.";

  return raw;
}

// Cookie helpers for single-client session enforcement (LOA-XXX).
// The cookie is set on the root domain so it's shared across all tenant subdomains.

export function setActiveTenantCookie(tenant: string): void {
  const domain = `.${BASE_DOMAIN}`;
  // Only add Secure flag on HTTPS - localhost typically uses HTTP
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${ACTIVE_TENANT_COOKIE}=${tenant}; domain=${domain}; path=/; SameSite=Lax${secure}`;
}

export function getActiveTenantCookie(): string | null {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${ACTIVE_TENANT_COOKIE}=([^;]*)`)
  );

  return match ? decodeURIComponent(match[1]) : null;
}

export function clearActiveTenantCookie(): void {
  const domain = `.${BASE_DOMAIN}`;
  document.cookie = `${ACTIVE_TENANT_COOKIE}=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

/**
 * Purge every client-side OAuth credential for this origin: the
 * react-oauth2-code-pkce tokens in localStorage (shared across tabs) and the
 * pending OAuth flow state in sessionStorage.
 *
 * Must run on every path that invalidates the session — including the 401
 * handler, since a server-side revocation (cross-tenant logout) leaves
 * `ROCP_token` behind, and `hasStoredOAuthToken()` would then hold the
 * protected layout on its initializing spinner instead of letting RouteGuard
 * redirect to login.
 *
 * CRITICAL: Do NOT call the library's logOut() instead — it writes NEW stale
 * PKCE state that breaks a fresh login in another tab.
 */
export function clearClientAuthState(): void {
  const ropKeys = Object.keys(localStorage).filter((k) => k.startsWith("ROCP_"));

  ropKeys.forEach((k) => localStorage.removeItem(k));
  sessionStorage.removeItem(OAUTH_STATE_KEY);
}

/**
 * Check if a token exists in localStorage from the react-oauth2-code-pkce library.
 *
 * This is a workaround for timing issues where the library hasn't yet read the
 * token from localStorage but the user is actually authenticated. Used to
 * prevent premature redirects to login when opening a new tab with a direct URL.
 *
 * The library uses "ROCP_" prefix for its storage keys and stores values as JSON.
 */
export function hasStoredOAuthToken(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  // react-oauth2-code-pkce stores tokens with ROCP_ prefix as JSON strings
  // Using localStorage (shared across tabs) per oauth.config.ts storage setting
  const token = localStorage.getItem("ROCP_token");
  const idToken = localStorage.getItem("ROCP_idToken");

  // Values are JSON-stringified, so '""' (empty string) or 'null' are invalid
  // A valid token looks like '"abc123..."' (quoted string with content)
  const hasToken = token !== null && token !== '""' && token !== "null" && token.length > 2;
  const hasIdToken = idToken !== null && idToken !== '""' && idToken !== "null";

  return hasToken || hasIdToken;
}
